import { type } from 'arktype'

import { type CalendarDate, calendarDateType, formatYyyyMmDd, tryParseYyyyMmDd } from '@orus.eu/calendar-date'
import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { failure, type Result, success } from '@orus.eu/result'

import {
  AbstractDimension,
  type DimensionMcpAdapter,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'

export class CalendarDateDimension<NAME extends string> extends AbstractDimension<NAME, CalendarDate> {
  override validateData(value: LooselyTypedValue): Result<CalendarDate, DimensionValidationProblem> {
    const result = calendarDateType(value)
    if (result instanceof type.errors) {
      return dimensionValidationFailure(`Field ${this.name} is not a CalendarDate: ${result.summary}`)
    }

    return success(result)
  }

  override formatToString(value: CalendarDate): string {
    return formatYyyyMmDd(value)
  }

  override readonly mcpAdapter: DimensionMcpAdapter<CalendarDate> = {
    parseValueFromMcp(mcpValueInput: string): Result<CalendarDate, string> {
      const parsingResult = tryParseYyyyMmDd(mcpValueInput)

      return parsingResult
        ? success(parsingResult)
        : failure(`Invalid date format: ${mcpValueInput}.
        For this tool, you must specify the date in the exact format yyyy-mm-dd.`)
    },
    formattingInstructions: `For this tool, you must specify the date in the exact format yyyy-mm-dd.`,
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<string> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.string(valueDocumentation))
    },
    convertStateValueToTrackingValue: (stateValue: CalendarDate | null | undefined): string | null => {
      if (!stateValue) return null
      return formatYyyyMmDd(stateValue)
    },
  }
}

/**
 * Check if the calendar date is fully filled.
 */
export function isCalendarDateFullyFilled(value: unknown): boolean {
  if (value == undefined) {
    return false
  }

  if (typeof value !== 'object' || value === null) {
    return false
  }

  return (
    'year' in value &&
    typeof value.year === 'number' &&
    'oneBasedMonth' in value &&
    typeof value.oneBasedMonth === 'number' &&
    'oneBasedDay' in value &&
    typeof value.oneBasedDay === 'number'
  )
}
